import merge from 'lodash.merge';
import { getOffersAsync, isOfferValid, addCartItemAsync } from 'Utils/cart';
import { STORE_ADDRESS } from 'Utils/constants';


const REDIRECT = true;
 
window.addEventListener('DOMContentLoaded', async () => {
  // Check window for EWI global
  window.EWI = window.EWI || {};

  // Get all cart buttons on page
  const cartButtons = Array.from(document.querySelectorAll('.cart-button'));

  try {
    // Get and validate offers via buttons and then return offers into EWI global
    const offers = await handleOffersOnPage(cartButtons);

    EWI.offers = offers;
  } catch(err) {
    console.error(err);
  } finally {
    cartButtons.forEach((button) => {
      // Add class to button to enable pointer events after offers have been processed
      button.classList.add('is-clickable');

      button.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        addItemViaAPIOrFallback(e);
      });
    });
  }
});


/**
 * Parse page for cart buttons and get offerOriginIds from their URL params.
 * Get and validate all unique offers found on page and apply that data to the dataLayer for GA4.
 * @param {*} cartButtons Array of all cart buttons on page
 * @returns Array of item data that is used with GA4 dataLayer
 */
const handleOffersOnPage = async (cartButtons) => {
  // Get total number of unique offers from buttons
  const offerIds = cartButtons.map(button => {
    return new URLSearchParams(new URL(button.href).search).get('offerId');
  });
  
  // Convert string Ids into numbers and then filter out non-numbers via Boolean test. Then sort Ids in ascending order to match
  // how getOffersAsync returns results
  const uniqueOfferIds = [...new Set(offerIds)].map(offerId => parseInt(offerId)).filter(Boolean).sort((a,b) => a - b);

  // Use getOffersAsync to collect json array of all offers on the page
  if(uniqueOfferIds.length) {
    const offers = await getOffersAsync(uniqueOfferIds.toString());

    const validOffers = offers.filter((offer, i) => {
      const valid = isOfferValid(offer.BeginDate, offer.EndDate);
      if(!valid) uniqueOfferIds.splice(i, 1); // Remove
      return valid;
    });

    if(validOffers.length) { // if we have valid offers
      const items = validOffers.map((offer, i) => {
        const offerId = uniqueOfferIds[i];
        const offerName = offer.Name;
        const offerPrice = offer.Price;
        const offerCategory = offer.CategoryId;
        const offerCode = offer.Code;
  
        return {
          item_id: String(offerId),
          item_name: offerName,
          item_category: offerCategory,
          item_code: offerCode,
          price: offerPrice.toFixed(2),
        }
      });

      let dataLayerItem = {
        'ecommerce': {
          'items': items
        }
      }

      // Determine if GA4 event will be view_item_list or view_item
      if(items.length > 1) {
        merge(dataLayerItem, {'event': 'view_item_list'});
        merge(dataLayerItem.ecommerce, {
          'item_list_id': document.title.toLocaleLowerCase().replace(/[^A-Z0-9]+/ig, "_"),
          'item_list_name': document.title
        });
      } else {
        merge(dataLayerItem, {'event': 'view_item'});
        merge(dataLayerItem.ecommerce, {
          'currency': 'USD',
          'value': items.reduce((n, {price}) => n + price, 0)
        });
      }

      // Send item event to GA4 data layer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(dataLayerItem);

      return items;
    } else {
      return [];
    }
  } else {
    return [];
  }
}


/**
 * Adds an item via the store API call or provides a direct link fallback if API fails.
 * Also configures the item selected event for GA4.
 * @param {*} event The cart button clicked event
 */
const addItemViaAPIOrFallback = async (event) => {
  const button = event.currentTarget;
  button.style.pointerEvents = "none";

  const btnUrl = new URL(button.href);
  const params = new URLSearchParams(btnUrl.search);
  const offerId = params.get('offerId');
  
  // Remove offerId after consuming because the name is changed for store
  params.delete('offerId');

  button.classList.add('is-loading');

  // Try to add to cart via API, if that fails then fallback to redirecting to cart using URL params to add item
  try {
    // Gets offer data and pushes to GA4 dataLayer for ecommerce tracking. Also determines if valid or expired offer.
    const validSelectedItem = EWI?.offers?.filter(offer => offer.item_id === offerId);
    const dataLayerItem = {
      'event': 'select_item',
      'ecommerce': {
        'item_list_id': document.title.toLocaleLowerCase().replace(/[^A-Z0-9]+/ig, "_"),
        'item_list_name': document.title,
        'items': validSelectedItem
      }
    }

    if(!validSelectedItem.length) {
      const redirectURL = `https://www.elliottwave.com/subscriptions?expired_offer=${offerId}`;
      console.log('Offer was expired! Redirecting...', redirectURL);
      if(REDIRECT) {
        window.location.href = redirectURL;
      }
      return;
    }

    // Send item event to GA4 data layer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataLayerItem);

    const response = await addCartItemAsync(offerId, params.toString());

    if(response?.CartGuid) {
      params.append('cart', response.CartGuid);
      const redirectURL = `${STORE_ADDRESS}/index?${params.toString()}&add_item=${offerId}`;

      console.log('Redirecting...', redirectURL);
      if(REDIRECT) {
        window.location.href = redirectURL;
      }
    } else {
      throw Error(response);
    }
  } catch (err) {
    //If store API calls (getOffers or addItemAsync) fail then fallback to manually adding item via URL with ewi store
    console.error(err);

    params.append('itemid', offerId);
    const redirectURL = `${STORE_ADDRESS}/index?${params.toString()}`;

    console.log('Fallback redirecting to store...', redirectURL);
    if(REDIRECT) {
      window.location.href = redirectURL;
    }
  } finally {
    button.classList.remove('is-loading');
    button.style.pointerEvents = "auto";
  }; 
}
